import { type FC, useState } from "react";
import { InputNumber } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import "./style.less";

export interface TreatmentDetailsProduct {
  id: string;
  name: string;
  rate: string;
}

export interface TreatmentDetailsProps {
  title: string;
  products: TreatmentDetailsProduct[];
  tankVolume?: number | null;
  onTankVolumeChange?: (value: number | null) => void;
  tankVolumeUnit?: string;
  tankVolumeMin?: number;
  tankVolumeMax?: number;
  instructions?: string;
  defaultExpanded?: boolean;
  className?: string;
}

const getTankVolumeError = (
  tankVolume: number | null | undefined,
  tankVolumeMin: number | undefined,
  tankVolumeMax: number | undefined,
  unit: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string | undefined => {
  if (tankVolume == null) {
    return undefined;
  }
  if (tankVolumeMin != null && tankVolume < tankVolumeMin) {
    return formatTranslation(
      t("newRatesAndDosage.treatmentDetails.tankVolumeErrorMin", {
        min: tankVolumeMin,
        unit,
      })
    );
  }
  if (tankVolumeMax != null && tankVolume > tankVolumeMax) {
    return formatTranslation(
      t("newRatesAndDosage.treatmentDetails.tankVolumeErrorMax", {
        max: tankVolumeMax,
        unit,
      })
    );
  }
  return undefined;
};

const TreatmentDetails: FC<TreatmentDetailsProps> = ({
  title,
  products,
  tankVolume,
  onTankVolumeChange,
  tankVolumeUnit,
  tankVolumeMin,
  tankVolumeMax,
  instructions,
  defaultExpanded = true,
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const unit =
    tankVolumeUnit ??
    formatTranslation(t("newRatesAndDosage.treatmentDetails.tankVolumeUnit"));

  const tankVolumeError = getTankVolumeError(
    tankVolume,
    tankVolumeMin,
    tankVolumeMax,
    unit,
    t
  );

  const tankVolumeRangeText =
    tankVolumeMin != null && tankVolumeMax != null
      ? `${tankVolumeMin} ${unit} - ${tankVolumeMax} ${unit}`
      : undefined;

  return (
    <div
      className={`rd-treatment-details ${className ?? ""}`}
      data-testid="treatment-details"
    >
      <div className="rd-treatment-details__header-row">
        <span
          className="rd-treatment-details__title"
          data-testid="treatment-details-title"
        >
          {title}
        </span>
        <button
          type="button"
          className="rd-treatment-details__toggle"
          aria-expanded={expanded}
          aria-label={formatTranslation(
            t(
              expanded
                ? "newRatesAndDosage.treatmentDetails.collapse"
                : "newRatesAndDosage.treatmentDetails.expand",
              { title }
            )
          )}
          onClick={() => {
            setExpanded((prev) => !prev);
          }}
        >
          <DownOutlined
            className={`rd-treatment-details__toggle-icon ${
              expanded ? "rd-treatment-details__toggle-icon--expanded" : ""
            }`}
          />
        </button>
      </div>
      <span className="rd-treatment-details__divider" aria-hidden="true" />
      {expanded && (
        <>
          <div className="rd-treatment-details__body">
            <div className="rd-treatment-details__products">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="rd-treatment-details__product"
                  data-testid="treatment-details-product"
                >
                  <span className="rd-treatment-details__product-name">
                    {product.name}
                  </span>
                  <span className="rd-treatment-details__product-rate">
                    {product.rate}
                  </span>
                </div>
              ))}
            </div>
            <div className="rd-treatment-details__tank-volume">
              <span className="rd-treatment-details__tank-label">
                {formatTranslation(
                  t("newRatesAndDosage.treatmentDetails.tankVolume")
                )}
              </span>
              <InputNumber
                className="rd-treatment-details__tank-input"
                status={tankVolumeError ? "error" : undefined}
                value={tankVolume}
                onChange={onTankVolumeChange}
                suffix={unit}
                controls={false}
                aria-invalid={Boolean(tankVolumeError)}
                aria-label={formatTranslation(
                  t("newRatesAndDosage.treatmentDetails.tankVolumeInput", {
                    title,
                  })
                )}
              />
              {tankVolumeError ? (
                <span
                  className="rd-treatment-details__tank-error"
                  data-testid="treatment-details-tank-error"
                >
                  {tankVolumeError}
                </span>
              ) : (
                tankVolumeRangeText && (
                  <span className="rd-treatment-details__tank-range">
                    {tankVolumeRangeText}
                  </span>
                )
              )}
            </div>
          </div>
          {instructions && (
            <p className="rd-treatment-details__instructions">{instructions}</p>
          )}
        </>
      )}
    </div>
  );
};

export default TreatmentDetails;
